import type { ViewTypeValues } from '@/utils/enums';

import { ActionType, Step, type Workflow } from '@repo/common';
import i18next from 'i18next';
import { useEffect, useState } from 'react';

import { setCustomerDataAction, setViewTypeAction, setWorkflowAction } from '@/providers/app-provider/actions';
import { useAppContext } from '@/providers/app-provider/context';
import { getInfo, getSessionByRedirectToken, postSubmitHandled } from '@/services/rivaas-api';
import { setRivaasSessionToken } from '@/services/rivaas-api/http-client';
import { ViewType } from '@/utils/enums';
import { stepToWorkflow } from '@/utils/step-workflow';

function normalizeViewType(viewType: string | null): ViewTypeValues {
  if (viewType === ViewType.IFRAME) {
    return ViewType.IFRAME;
  }

  return ViewType.FULL_PAGE;
}

function getUrlParams() {
  const urlParams = new URLSearchParams(window.location.search);

  const sessionToken = urlParams.get('sessionToken') ?? undefined;
  const redirectToken = urlParams.get('redirectToken') ?? undefined;
  const viewType = normalizeViewType(urlParams.get('viewType'));
  const locale = urlParams.get('locale') ?? undefined;
  const logoUrl = urlParams.get('logoUrl') ?? undefined;
  const verifiedUrl = urlParams.get('verifiedUrl') ?? undefined;
  const unverifiedUrl = urlParams.get('unverifiedUrl') ?? undefined;
  const rejectedUrl = urlParams.get('rejectedUrl') ?? undefined;

  return {
    locale,
    logoUrl,
    redirectToken,
    sessionToken,
    viewType,
    verifiedUrl,
    unverifiedUrl,
    rejectedUrl,
  } as const;
}

const introWorkflow: Workflow = {
  nextStep: {
    action: { data: {}, type: ActionType.PROCESS },
    value: Step.INTRO,
  },
};

export function useAuthorize() {
  const { dispatch } = useAppContext();

  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    async function init() {
      const { locale, logoUrl, redirectToken, rejectedUrl, sessionToken, unverifiedUrl, verifiedUrl, viewType } =
        getUrlParams();

      dispatch(setViewTypeAction(viewType));
      dispatch(setWorkflowAction(introWorkflow));

      let resolvedSessionToken = sessionToken;

      if (!resolvedSessionToken && redirectToken) {
        try {
          const data = await getSessionByRedirectToken(redirectToken);

          resolvedSessionToken = data.sessionToken;
        } catch {
          // fall through to unauthorized state below
        }
      }

      if (!resolvedSessionToken) {
        // eslint-disable-next-line
        console.warn('sessionToken not found');

        setIsAuthorized(false);
        setLoading(false);

        return;
      }

      setRivaasSessionToken(resolvedSessionToken);

      let resolvedFirstStep: string | undefined = undefined;
      let resolvedLocale = locale;
      let resolvedLogoUrl = logoUrl;
      let resolvedVerifiedUrl = verifiedUrl;
      let resolvedUnverifiedUrl = unverifiedUrl;
      let resolvedRejectedUrl = rejectedUrl;

      // If redirectToken is present, we're coming from mobile-redirect.
      // Bootstrap the workflow by calling BFF with the redirectToken, so the server advances past `mobile_redirect`.
      if (redirectToken) {
        try {
          const load = async () => postSubmitHandled(undefined, { redirectToken });

          let data = await load();

          // Race protection: if desktop hasn't registered the redirectToken yet, BFF may keep us on `mobile_redirect` for the first call.
          // One short retry typically resolves it and allows the mobile client to skip the UI step.
          if (data.nextStep.step === Step.MOBILE_REDIRECT) {
            await new Promise((resolve) => setTimeout(resolve, 300));
            data = await load();
          }

          resolvedFirstStep = data.nextStep.step;

          const workflowBase = stepToWorkflow(data.nextStep.step, {
            data: data.nextStep.data,
            unverifiedUrl: resolvedUnverifiedUrl,
            verifiedUrl: resolvedVerifiedUrl,
          });

          if (workflowBase) {
            dispatch(setWorkflowAction(workflowBase));
          }
        } catch {
          // fallback to URL-based behavior below
        }
      }

      // fetch branding and locale via GET /info; use steps[0] as the first step to render
      if (!redirectToken) {
        try {
          const data = await getInfo();

          resolvedFirstStep = data.steps[0]?.step ?? resolvedFirstStep;
          resolvedLocale = data.locale || locale;
          resolvedLogoUrl = data.logoUrl ?? logoUrl;
          resolvedVerifiedUrl = data.verifiedUrl || verifiedUrl;
          resolvedUnverifiedUrl = data.unverifiedUrl || unverifiedUrl;
          resolvedRejectedUrl = data.rejectedUrl || rejectedUrl;

          // Keep the workflow-level mobile_redirect settings available for the INTRO step.
          // We only care about `skippable` for the mobile redirect step.
          const mobileRedirect = data.steps.find((s) => s.step === 'mobile_redirect');
          const mobileRedirectSkippable = mobileRedirect?.skippable;

          dispatch(
            setCustomerDataAction({
              firstStep: resolvedFirstStep,
              firstStepData: data.steps[0]?.data,
              logo: resolvedLogoUrl,
              locale: resolvedLocale,
              name: '',
              mobileRedirectSkippable,
              redirect:
                resolvedVerifiedUrl || resolvedUnverifiedUrl || resolvedRejectedUrl
                  ? {
                      verifiedUrl: resolvedVerifiedUrl ?? '',
                      unverifiedUrl: resolvedUnverifiedUrl ?? '',
                      rejectedUrl: resolvedRejectedUrl ?? '',
                    }
                  : undefined,
            }),
          );

          setIsAuthorized(true);
          setLoading(false);

          return;
        } catch {
          // fallback: continue with URL param values
        }
      }

      if (resolvedLocale) {
        void i18next.changeLanguage(resolvedLocale);
      }

      dispatch(
        setCustomerDataAction({
          firstStep: resolvedFirstStep,
          logo: resolvedLogoUrl,
          locale: resolvedLocale,
          name: '',
          // no info loaded: fall back to default FE behavior (mobile_redirect skippable by default mapping)
          mobileRedirectSkippable: undefined,
          redirect:
            resolvedVerifiedUrl || resolvedUnverifiedUrl || resolvedRejectedUrl
              ? {
                  verifiedUrl: resolvedVerifiedUrl ?? '',
                  unverifiedUrl: resolvedUnverifiedUrl ?? '',
                  rejectedUrl: resolvedRejectedUrl ?? '',
                }
              : undefined,
        }),
      );

      setIsAuthorized(true);
      setLoading(false);
    }

    void init();
  }, [dispatch]);

  return { loading, isAuthorized } as const;
}
